import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  DEFAULT_GTM_ID,
  identifyPendo,
  initializeGTM,
  initializePendo,
  loadPendoScript,
  pushGTMEvent,
  trackPendoEvent,
} from 'src/utils/analytics';

import { useOptionalSession } from './SessionContext';

// ============================================================================
// Types
// ============================================================================

interface AnalyticsContextValue {
  /**
   * Push an event to Google Tag Manager dataLayer
   */
  pushGTMEvent: (event: string, data?: Record<string, unknown>) => void;
  /**
   * Track an event in Pendo
   */
  trackPendo: (eventName: string, metadata?: Record<string, unknown>) => void;
}

// ============================================================================
// Context
// ============================================================================

function noopGTM(event: string, data?: Record<string, unknown>) {
  if (import.meta.env.MODE === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[Analytics] GTM event:', event, data);
  }
}

function noopPendo(eventName: string, metadata?: Record<string, unknown>) {
  if (import.meta.env.MODE === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[Analytics] Pendo track:', eventName, metadata);
  }
}

const AnalyticsContext = createContext<AnalyticsContextValue>({
  pushGTMEvent: noopGTM,
  trackPendo: noopPendo,
});

/**
 * AnalyticsProvider initializes analytics services (GTM, Pendo) when
 * user and session data are available.
 */
export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, session } = useOptionalSession();
  const pendoInitializedRef = useRef(false);

  // Get GTM ID from session or use default
  const gtmId = session?.gtmContainerId || DEFAULT_GTM_ID;

  // Initialize GTM
  useEffect(() => {
    if (gtmId) {
      initializeGTM(gtmId);
    }
  }, [gtmId]);

  // Initialize Pendo only when user is logged in
  // Dev/staging IDs are prefixed to keep data separate from production
  // @see https://support.pendo.io/hc/en-us/articles/360031862352-Pendo-in-multiple-environments-for-development-and-testing
  useEffect(() => {
    // Don't load Pendo in Meticulous testing environment
    if (window.location.hostname.includes('meticulous')) return;

    // Don't load Pendo at all if no user is logged in
    if (!user?.id || !session?.id) return;

    // Load the Pendo script (only once we have a user)
    loadPendoScript();

    if (!pendoInitializedRef.current) {
      initializePendo(user, session);
      pendoInitializedRef.current = true;
    } else {
      // Re-identify if user changes
      identifyPendo(user, session);
    }
  }, [user, session]);

  const value = useMemo(
    () => ({
      pushGTMEvent: (event: string, data?: Record<string, unknown>) => {
        if (gtmId) {
          pushGTMEvent(event, data);
        }
      },
      trackPendo: trackPendoEvent,
    }),
    [gtmId]
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * useAnalytics provides access to analytics tracking functions.
 */
export function useAnalytics(): AnalyticsContextValue {
  return useContext(AnalyticsContext);
}

// ============================================================================
// Backward Compatibility - GTM exports
// ============================================================================

/**
 * @deprecated Use AnalyticsProvider instead
 */
export const GTMProvider = AnalyticsProvider;

/**
 * @deprecated Use useAnalytics().pushGTMEvent instead
 */
export function useGTM() {
  const { pushGTMEvent } = useAnalytics();
  return { pushEvent: pushGTMEvent };
}
